// components/admin/TypeLinksComponent.js - Fixed infinite loop issues
import React, { useState, useEffect, useRef, useCallback } from "react"

const TypeLinksComponent = ({
  customTypes,
  onChange,
  setLoading,
  initialLinks = [],
  allMaterials = [],
}) => {
  // State for type links
  const [typeLinks, setTypeLinks] = useState(initialLinks)

  // UI state
  const [showLinkForm, setShowLinkForm] = useState(false)
  const [editingLinkIndex, setEditingLinkIndex] = useState(null)

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    linkedTechnicalNames: [],
    specificMappings: {},
  })

  const [showSpecificMappings, setShowSpecificMappings] = useState(false)

  // CORREZIONE: Usa useRef per evitare loop infiniti
  const previousInitialLinksRef = useRef()
  const isInitializedRef = useRef(false)

  // CORREZIONE: Memoizza onChange per evitare che cambi ad ogni render
  const memoizedOnChange = useCallback(onChange, [])

  // CORREZIONE: Initialize from props - con controlli anti-loop
  useEffect(() => {
    // Solo se initialLinks è effettivamente cambiato
    const currentInitialLinks = JSON.stringify(initialLinks)
    const previousInitialLinks = previousInitialLinksRef.current

    if (currentInitialLinks !== previousInitialLinks) {
      console.log("🔧 TypeLinks: initialLinks changed, updating typeLinks")
      setTypeLinks([...initialLinks]) // Copia profonda per evitare reference issues
      previousInitialLinksRef.current = currentInitialLinks
      isInitializedRef.current = true
    }
  }, [initialLinks])

  // CORREZIONE: Pass changes up to parent - con controlli anti-loop
  useEffect(() => {
    // Solo se è stato inizializzato e onChange è disponibile
    if (isInitializedRef.current && memoizedOnChange) {
      // Confronta se i typeLinks sono effettivamente diversi da quelli iniziali
      const currentLinks = JSON.stringify(typeLinks)
      const initialLinksStr = JSON.stringify(initialLinks)

      if (currentLinks !== initialLinksStr) {
        console.log("🔧 TypeLinks: calling onChange with updated links")
        memoizedOnChange([...typeLinks]) // Copia profonda
      }
    }
  }, [typeLinks, memoizedOnChange, initialLinks])

  // Check if link form is valid
  const isLinkFormValid = () => {
    return formData.name && formData.linkedTechnicalNames.length >= 2
  }

  // Add a new link
  const handleAddLink = (e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()

    setEditingLinkIndex(null)
    setFormData({
      name: "",
      linkedTechnicalNames: [],
      specificMappings: {},
    })
    setShowSpecificMappings(false)
    setShowLinkForm(true)
  }

  // Edit an existing link
  const handleEditLink = (e, index) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()

    setEditingLinkIndex(index)
    const linkToEdit = typeLinks[index]
    setFormData({
      name: linkToEdit.name || "",
      linkedTechnicalNames: [...(linkToEdit.linkedTechnicalNames || [])], // Copia profonda
      specificMappings: { ...(linkToEdit.specificMappings || {}) }, // Copia profonda
    })
    setShowSpecificMappings(false)
    setShowLinkForm(true)
  }

  // Delete a link
  const handleDeleteLink = (e, index) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()

    if (window.confirm("Are you sure you want to delete this link?")) {
      const updatedLinks = [...typeLinks]
      updatedLinks.splice(index, 1)
      setTypeLinks(updatedLinks)
    }
  }

  // Handle input change in the form
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Handle checking/unchecking technical types
  const handleTypeToggle = (technicalName) => {
    setFormData((prev) => {
      const linkedTechnicalNames = [...prev.linkedTechnicalNames]
      const index = linkedTechnicalNames.indexOf(technicalName)

      if (index === -1) {
        linkedTechnicalNames.push(technicalName)
      } else {
        linkedTechnicalNames.splice(index, 1)
        // Rimuovi anche dai specificMappings se non più linkato
        const newSpecificMappings = { ...prev.specificMappings }
        delete newSpecificMappings[technicalName]
        // Rimuovi anche dalle mappature che referenziano questo tipo
        Object.keys(newSpecificMappings).forEach((sourceType) => {
          if (
            newSpecificMappings[sourceType] &&
            newSpecificMappings[sourceType][technicalName]
          ) {
            delete newSpecificMappings[sourceType][technicalName]
          }
        })

        return {
          ...prev,
          linkedTechnicalNames,
          specificMappings: newSpecificMappings,
        }
      }

      return {
        ...prev,
        linkedTechnicalNames,
      }
    })
  }

  // Gestione specificMappings
  const handleAddSpecificMapping = (
    sourceType,
    targetType,
    sourceMaterial,
    targetMaterial
  ) => {
    setFormData((prev) => {
      const newSpecificMappings = { ...prev.specificMappings }

      if (!newSpecificMappings[sourceType]) {
        newSpecificMappings[sourceType] = {}
      }
      if (!newSpecificMappings[sourceType][targetType]) {
        newSpecificMappings[sourceType][targetType] = {}
      }

      newSpecificMappings[sourceType][targetType][sourceMaterial] =
        targetMaterial

      return {
        ...prev,
        specificMappings: newSpecificMappings,
      }
    })
  }

  // Rimuovi specificMapping
  const handleRemoveSpecificMapping = (
    sourceType,
    targetType,
    sourceMaterial
  ) => {
    setFormData((prev) => {
      const newSpecificMappings = { ...prev.specificMappings }

      if (
        newSpecificMappings[sourceType] &&
        newSpecificMappings[sourceType][targetType]
      ) {
        delete newSpecificMappings[sourceType][targetType][sourceMaterial]

        // Pulisci strutture vuote
        if (
          Object.keys(newSpecificMappings[sourceType][targetType]).length === 0
        ) {
          delete newSpecificMappings[sourceType][targetType]
        }
        if (Object.keys(newSpecificMappings[sourceType]).length === 0) {
          delete newSpecificMappings[sourceType]
        }
      }

      return {
        ...prev,
        specificMappings: newSpecificMappings,
      }
    })
  }

  // Ottieni materiali per tipo tecnico
  const getMaterialsForType = (technicalType) => {
    const typeData = customTypes.find((t) => t.technicalName === technicalType)
    if (!typeData) return []

    return [...typeData.standardMaterials, ...typeData.optionalMaterials]
      .filter((material, index, array) => array.indexOf(material) === index) // rimuovi duplicati
      .sort()
  }

  // Save the form data
  const handleSaveForm = (e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()

    if (!isLinkFormValid()) {
      alert("Please enter a name and select at least two technical types.")
      return
    }

    const updatedLinks = [...typeLinks]

    if (editingLinkIndex !== null) {
      updatedLinks[editingLinkIndex] = { ...formData } // Copia profonda
    } else {
      updatedLinks.push({ ...formData }) // Copia profonda
    }

    setTypeLinks(updatedLinks)
    setShowLinkForm(false)
    setShowSpecificMappings(false)
  }

  // Handle modal close
  const handleCloseForm = (e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    setShowLinkForm(false)
    setShowSpecificMappings(false)
  }

  return (
    <div>
      <div className="ay-flex ay-justify-between ay-items-center ay-mb-4">
        <h3 className="ay-text-lg ay-font-medium">Type Links</h3>
        <button
          type="button"
          onClick={handleAddLink}
          className="ay-px-3 ay-py-1 ay-bg-blue-500 ay-text-white ay-rounded ay-text-sm ay-hover:ay-bg-blue-600"
        >
          Add Link
        </button>
      </div>

      {/* Type Links List */}
      {typeLinks.length > 0 ? (
        <div className="ay-space-y-4">
          {typeLinks.map((link, index) => (
            <div
              key={index}
              className="ay-border ay-rounded-lg ay-p-4 ay-bg-white ay-shadow-sm"
            >
              <div className="ay-flex ay-justify-between ay-items-start">
                <div className="ay-flex-1">
                  <h4 className="ay-font-medium ay-text-lg">{link.name}</h4>
                  <div className="ay-flex ay-flex-wrap ay-gap-1 ay-mt-1">
                    {link.linkedTechnicalNames?.map((type) => {
                      const typeObj = customTypes.find(
                        (t) => t.technicalName === type
                      )
                      return (
                        <span
                          key={type}
                          className="ay-px-2 ay-py-1 ay-text-xs ay-bg-blue-100 ay-text-blue-800 ay-rounded"
                        >
                          {typeObj?.displayLabel || type}
                        </span>
                      )
                    })}
                  </div>

                  {/* Mostra info su specificMappings */}
                  {link.specificMappings &&
                    Object.keys(link.specificMappings).length > 0 && (
                      <div className="ay-mt-2 ay-p-2 ay-bg-yellow-50 ay-rounded">
                        <p className="ay-text-xs ay-text-yellow-800 ay-font-medium">
                          🔗 Specific Mappings Configured
                        </p>
                        <div className="ay-text-xs ay-text-yellow-700 ay-mt-1">
                          {Object.entries(link.specificMappings).map(
                            ([sourceType, targets]) => (
                              <div key={sourceType}>
                                <strong>{sourceType}</strong> →{" "}
                                {Object.keys(targets).join(", ")} (
                                {Object.values(targets).reduce(
                                  (acc, curr) => acc + Object.keys(curr).length,
                                  0
                                )}{" "}
                                mappings)
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>

                <div className="ay-flex ay-gap-2">
                  <button
                    type="button"
                    onClick={(e) => handleEditLink(e, index)}
                    className="ay-px-2 ay-py-1 ay-text-xs ay-bg-yellow-500 ay-text-white ay-rounded ay-hover:ay-bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleDeleteLink(e, index)}
                    className="ay-px-2 ay-py-1 ay-text-xs ay-bg-red-500 ay-text-white ay-rounded ay-hover:ay-bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="ay-mt-3 ay-pt-2 ay-border-t ay-border-gray-200">
                <p className="ay-text-sm ay-text-gray-500">
                  This link will automatically apply the same material to all
                  linked types. Specific mappings allow different materials to
                  be applied.
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="ay-bg-gray-50 ay-p-6 ay-rounded-lg ay-text-center ay-text-gray-500">
          No type links defined. Click "Add Link" to create your first type
          link.
        </div>
      )}

      {/* Link Form Modal */}
      {showLinkForm && (
        <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
          <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-6xl ay-max-h-screen ay-overflow-auto">
            <h3 className="ay-text-lg ay-font-bold ay-mb-4">
              {editingLinkIndex !== null ? "Edit" : "Add"} Type Link
            </h3>

            <div>
              <div className="ay-mb-4">
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                  Link Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleSaveForm(e)
                    }
                  }}
                />
              </div>

              {/* Technical Types Selection */}
              <div className="ay-mb-4">
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                  Linked Technical Types *
                </label>
                <p className="ay-text-xs ay-text-gray-500 ay-mb-2">
                  Select at least two technical types to link together.
                </p>

                <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-3 lg:ay-grid-cols-4 ay-gap-2 ay-p-3 ay-border ay-border-gray-300 ay-rounded-md">
                  {customTypes.map((type) => (
                    <label
                      key={type.technicalName}
                      className={`ay-flex ay-items-center ay-gap-2 ay-px-2 ay-py-1 ay-rounded ay-cursor-pointer ay-text-sm ${
                        formData.linkedTechnicalNames.includes(
                          type.technicalName
                        )
                          ? "ay-bg-blue-100 ay-text-blue-800"
                          : "ay-bg-gray-100 ay-text-gray-700"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.linkedTechnicalNames.includes(
                          type.technicalName
                        )}
                        onChange={() => handleTypeToggle(type.technicalName)}
                        className="ay-h-4 ay-w-4 ay-text-blue-600 ay-rounded ay-focus:ay-ring-blue-500"
                      />
                      <span>{type.displayLabel || type.technicalName}</span>
                    </label>
                  ))}

                  {customTypes.length === 0 && (
                    <div className="ay-col-span-full ay-text-center ay-py-4 ay-text-gray-500">
                      No technical types available. Add material types first.
                    </div>
                  )}
                </div>

                {formData.linkedTechnicalNames.length < 2 && (
                  <p className="ay-text-xs ay-text-red-500 ay-mt-1">
                    Please select at least two technical types.
                  </p>
                )}
              </div>

              {/* Specific Mappings Section */}
              {formData.linkedTechnicalNames.length >= 2 && (
                <div className="ay-mb-4">
                  <div className="ay-flex ay-justify-between ay-items-center ay-mb-2">
                    <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700">
                      Specific Material Mappings (Optional)
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setShowSpecificMappings(!showSpecificMappings)
                      }
                      className={`ay-px-3 ay-py-1 ay-rounded ay-text-sm ay-transition-colors ${
                        showSpecificMappings
                          ? "ay-bg-orange-500 ay-text-white ay-hover:ay-bg-orange-600"
                          : "ay-bg-gray-500 ay-text-white ay-hover:ay-bg-gray-600"
                      }`}
                    >
                      {showSpecificMappings
                        ? "Hide Mappings"
                        : "Configure Mappings"}
                    </button>
                  </div>
                  <p className="ay-text-xs ay-text-gray-500 ay-mb-2">
                    Define specific material correspondences between different
                    technical types. When not specified, materials with the same
                    name will be linked automatically.
                  </p>

                  {showSpecificMappings && (
                    <ImprovedSpecificMappingsEditor
                      linkedTypes={formData.linkedTechnicalNames}
                      customTypes={customTypes}
                      specificMappings={formData.specificMappings}
                      onAddMapping={handleAddSpecificMapping}
                      onRemoveMapping={handleRemoveSpecificMapping}
                      getMaterialsForType={getMaterialsForType}
                    />
                  )}
                </div>
              )}

              <div className="ay-flex ay-justify-end ay-gap-3 ay-mt-6">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="ay-px-4 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveForm}
                  className={`ay-px-4 ay-py-2 ay-border ay-border-transparent ay-rounded-md ay-shadow-sm ay-text-white ${
                    isLinkFormValid()
                      ? "ay-bg-blue-500 ay-hover:ay-bg-blue-600"
                      : "ay-bg-gray-400 ay-cursor-not-allowed"
                  }`}
                  disabled={!isLinkFormValid()}
                >
                  {editingLinkIndex !== null ? "Update" : "Create"} Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Componente per l'editing dei specific mappings
const ImprovedSpecificMappingsEditor = ({
  linkedTypes,
  customTypes,
  specificMappings,
  onAddMapping,
  onRemoveMapping,
  getMaterialsForType,
}) => {
  const [selectedSourceType, setSelectedSourceType] = useState("")
  const [selectedTargetType, setSelectedTargetType] = useState("")
  const [mappingMode, setMappingMode] = useState("select")

  // Reset selections when linked types change
  useEffect(() => {
    if (!linkedTypes.includes(selectedSourceType)) {
      setSelectedSourceType("")
    }
    if (!linkedTypes.includes(selectedTargetType)) {
      setSelectedTargetType("")
    }
  }, [linkedTypes, selectedSourceType, selectedTargetType])

  const handleMaterialMapping = (sourceMaterial, targetMaterial) => {
    if (
      selectedSourceType &&
      selectedTargetType &&
      sourceMaterial &&
      targetMaterial
    ) {
      onAddMapping(
        selectedSourceType,
        selectedTargetType,
        sourceMaterial,
        targetMaterial
      )
    }
  }

  const getMappingsForPair = (sourceType, targetType) => {
    return specificMappings[sourceType]?.[targetType] || {}
  }

  return (
    <div className="ay-border ay-border-gray-300 ay-rounded-lg ay-p-4 ay-bg-gray-50">
      <h4 className="ay-font-medium ay-mb-4 ay-text-center">
        🔗 Material Mappings Configuration
      </h4>

      {/* MODE SELECTOR */}
      <div className="ay-flex ay-justify-center ay-mb-4">
        <div className="ay-bg-white ay-rounded-lg ay-p-1 ay-shadow-sm">
          <button
            type="button"
            onClick={() => setMappingMode("select")}
            className={`ay-px-4 ay-py-2 ay-rounded-md ay-text-sm ay-transition-colors ${
              mappingMode === "select"
                ? "ay-bg-blue-500 ay-text-white"
                : "ay-text-gray-600 ay-hover:ay-text-gray-800"
            }`}
          >
            📋 View & Manage
          </button>
          <button
            type="button"
            onClick={() => setMappingMode("add")}
            className={`ay-px-4 ay-py-2 ay-rounded-md ay-text-sm ay-transition-colors ${
              mappingMode === "add"
                ? "ay-bg-green-500 ay-text-white"
                : "ay-text-gray-600 ay-hover:ay-text-gray-800"
            }`}
          >
            ➕ Add Mappings
          </button>
        </div>
      </div>

      {/* EXISTING MAPPINGS OVERVIEW */}
      {mappingMode === "select" && (
        <div className="ay-mb-4">
          {Object.keys(specificMappings).length > 0 ? (
            <div className="ay-space-y-3">
              <h5 className="ay-font-medium ay-text-sm ay-text-center ay-mb-3">
                📊 Current Mappings Overview
              </h5>
              {Object.entries(specificMappings).map(([sourceType, targets]) =>
                Object.entries(targets).map(([targetType, materials]) => (
                  <div
                    key={`${sourceType}-${targetType}`}
                    className="ay-bg-white ay-rounded-lg ay-p-3 ay-border"
                  >
                    <div className="ay-flex ay-justify-between ay-items-center ay-mb-2">
                      <h6 className="ay-font-medium ay-text-sm">
                        {sourceType} → {targetType}
                      </h6>
                      <span className="ay-bg-blue-100 ay-text-blue-800 ay-text-xs ay-px-2 ay-py-1 ay-rounded">
                        {Object.keys(materials).length} mappings
                      </span>
                    </div>
                    <div className="ay-space-y-1">
                      {Object.entries(materials).map(
                        ([sourceMaterial, targetMaterial]) => (
                          <div
                            key={sourceMaterial}
                            className="ay-flex ay-items-center ay-justify-between ay-bg-gray-50 ay-p-2 ay-rounded ay-text-xs"
                          >
                            <span>
                              <strong>{sourceMaterial}</strong> →{" "}
                              <strong>{targetMaterial}</strong>
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                onRemoveMapping(
                                  sourceType,
                                  targetType,
                                  sourceMaterial
                                )
                              }
                              className="ay-px-2 ay-py-1 ay-bg-red-500 ay-text-white ay-rounded ay-hover:ay-bg-red-600"
                            >
                              ✕
                            </button>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="ay-text-center ay-py-6 ay-text-gray-500">
              <p className="ay-text-sm">
                📝 No specific mappings configured yet.
              </p>
              <p className="ay-text-xs ay-mt-1">
                Switch to "Add Mappings" mode to create material
                correspondences.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ADD MAPPINGS MODE */}
      {mappingMode === "add" && (
        <div>
          {/* TYPE PAIR SELECTOR */}
          <div className="ay-bg-white ay-rounded-lg ay-p-4 ay-mb-4 ay-border">
            <h5 className="ay-font-medium ay-text-sm ay-mb-3 ay-text-center">
              1️⃣ Select Type Pair to Map
            </h5>
            <div className="ay-grid ay-grid-cols-2 ay-gap-4">
              <div>
                <label className="ay-block ay-text-xs ay-font-medium ay-text-gray-700 ay-mb-2">
                  Source Type
                </label>
                <div className="ay-space-y-1">
                  {linkedTypes.map((type) => (
                    <label
                      key={type}
                      className={`ay-flex ay-items-center ay-gap-2 ay-p-2 ay-rounded ay-cursor-pointer ay-text-sm ay-transition-colors ${
                        selectedSourceType === type
                          ? "ay-bg-blue-100 ay-border-blue-500 ay-border"
                          : "ay-bg-gray-100 ay-hover:ay-bg-gray-200"
                      }`}
                    >
                      <input
                        type="radio"
                        name="sourceType"
                        checked={selectedSourceType === type}
                        onChange={() => setSelectedSourceType(type)}
                        className="ay-h-4 ay-w-4 ay-text-blue-600"
                      />
                      <span>{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="ay-block ay-text-xs ay-font-medium ay-text-gray-700 ay-mb-2">
                  Target Type
                </label>
                <div className="ay-space-y-1">
                  {linkedTypes
                    .filter((type) => type !== selectedSourceType)
                    .map((type) => (
                      <label
                        key={type}
                        className={`ay-flex ay-items-center ay-gap-2 ay-p-2 ay-rounded ay-cursor-pointer ay-text-sm ay-transition-colors ${
                          selectedTargetType === type
                            ? "ay-bg-green-100 ay-border-green-500 ay-border"
                            : "ay-bg-gray-100 ay-hover:ay-bg-gray-200"
                        }`}
                      >
                        <input
                          type="radio"
                          name="targetType"
                          checked={selectedTargetType === type}
                          onChange={() => setSelectedTargetType(type)}
                          className="ay-h-4 ay-w-4 ay-text-green-600"
                        />
                        <span>{type}</span>
                      </label>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* MATERIAL MAPPING INTERFACE */}
          {selectedSourceType && selectedTargetType && (
            <div className="ay-bg-white ay-rounded-lg ay-p-4 ay-border">
              <h5 className="ay-font-medium ay-text-sm ay-mb-3 ay-text-center">
                2️⃣ Map Materials:{" "}
                <span className="ay-text-blue-600">{selectedSourceType}</span> →{" "}
                <span className="ay-text-green-600">{selectedTargetType}</span>
              </h5>

              {/* Existing mappings for this pair */}
              {Object.keys(
                getMappingsForPair(selectedSourceType, selectedTargetType)
              ).length > 0 && (
                <div className="ay-mb-4 ay-p-3 ay-bg-yellow-50 ay-rounded ay-border-yellow-200">
                  <p className="ay-text-xs ay-font-medium ay-text-yellow-800 ay-mb-2">
                    Current mappings for this pair:
                  </p>
                  <div className="ay-space-y-1">
                    {Object.entries(
                      getMappingsForPair(selectedSourceType, selectedTargetType)
                    ).map(([source, target]) => (
                      <div
                        key={source}
                        className="ay-flex ay-items-center ay-justify-between ay-text-xs ay-bg-white ay-p-2 ay-rounded"
                      >
                        <span>
                          <strong>{source}</strong> → <strong>{target}</strong>
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            onRemoveMapping(
                              selectedSourceType,
                              selectedTargetType,
                              source
                            )
                          }
                          className="ay-px-1 ay-py-0.5 ay-bg-red-500 ay-text-white ay-rounded ay-text-xs ay-hover:ay-bg-red-600"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* MATERIAL MAPPING GRID */}
              <MaterialMappingGrid
                sourceType={selectedSourceType}
                targetType={selectedTargetType}
                sourceMaterials={getMaterialsForType(selectedSourceType)}
                targetMaterials={getMaterialsForType(selectedTargetType)}
                existingMappings={getMappingsForPair(
                  selectedSourceType,
                  selectedTargetType
                )}
                onAddMapping={handleMaterialMapping}
              />
            </div>
          )}

          {/* HELP TEXT */}
          {(!selectedSourceType || !selectedTargetType) && (
            <div className="ay-text-center ay-py-4 ay-text-gray-500">
              <p className="ay-text-sm">
                👆 Select both source and target types to start mapping
                materials
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Grid visuale per mappare i materiali
const MaterialMappingGrid = ({
  sourceType,
  targetType,
  sourceMaterials,
  targetMaterials,
  existingMappings,
  onAddMapping,
}) => {
  const [selectedSourceMaterial, setSelectedSourceMaterial] = useState("")
  const [selectedTargetMaterial, setSelectedTargetMaterial] = useState("")

  const handleAddMapping = () => {
    if (selectedSourceMaterial && selectedTargetMaterial) {
      onAddMapping(selectedSourceMaterial, selectedTargetMaterial)
      setSelectedSourceMaterial("")
      setSelectedTargetMaterial("")
    }
  }

  const isAlreadyMapped = (sourceMaterial) => {
    return existingMappings[sourceMaterial] !== undefined
  }

  return (
    <div>
      <div className="ay-grid ay-grid-cols-2 ay-gap-4 ay-mb-4">
        {/* SOURCE MATERIALS */}
        <div>
          <h6 className="ay-text-xs ay-font-medium ay-text-blue-700 ay-mb-2 ay-text-center">
            📦 {sourceType} Materials
          </h6>
          <div className="ay-max-h-48 ay-overflow-y-auto ay-space-y-1 ay-p-2 ay-bg-blue-50 ay-rounded">
            {sourceMaterials.map((material) => (
              <label
                key={material}
                className={`ay-flex ay-items-center ay-gap-2 ay-p-2 ay-rounded ay-cursor-pointer ay-text-xs ay-transition-colors ${
                  isAlreadyMapped(material)
                    ? "ay-bg-gray-200 ay-text-gray-500 ay-cursor-not-allowed"
                    : selectedSourceMaterial === material
                    ? "ay-bg-blue-200 ay-border-blue-500 ay-border"
                    : "ay-bg-white ay-hover:ay-bg-blue-100"
                }`}
              >
                <input
                  type="radio"
                  name="sourceMaterial"
                  checked={selectedSourceMaterial === material}
                  onChange={() =>
                    !isAlreadyMapped(material) &&
                    setSelectedSourceMaterial(material)
                  }
                  disabled={isAlreadyMapped(material)}
                  className="ay-h-3 ay-w-3 ay-text-blue-600"
                />
                <span
                  className={isAlreadyMapped(material) ? "ay-line-through" : ""}
                >
                  {material}
                </span>
                {isAlreadyMapped(material) && (
                  <span className="ay-text-xs ay-text-gray-500">
                    → {existingMappings[material]}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* TARGET MATERIALS */}
        <div>
          <h6 className="ay-text-xs ay-font-medium ay-text-green-700 ay-mb-2 ay-text-center">
            📦 {targetType} Materials
          </h6>
          <div className="ay-max-h-48 ay-overflow-y-auto ay-space-y-1 ay-p-2 ay-bg-green-50 ay-rounded">
            {targetMaterials.map((material) => (
              <label
                key={material}
                className={`ay-flex ay-items-center ay-gap-2 ay-p-2 ay-rounded ay-cursor-pointer ay-text-xs ay-transition-colors ${
                  selectedTargetMaterial === material
                    ? "ay-bg-green-200 ay-border-green-500 ay-border"
                    : "ay-bg-white ay-hover:ay-bg-green-100"
                }`}
              >
                <input
                  type="radio"
                  name="targetMaterial"
                  checked={selectedTargetMaterial === material}
                  onChange={() => setSelectedTargetMaterial(material)}
                  className="ay-h-3 ay-w-3 ay-text-green-600"
                />
                <span>{material}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* MAPPING ACTION */}
      <div className="ay-text-center ay-border-t ay-pt-3">
        {selectedSourceMaterial && selectedTargetMaterial ? (
          <div>
            <p className="ay-text-sm ay-mb-2">
              Create mapping:{" "}
              <strong className="ay-text-blue-600">
                {selectedSourceMaterial}
              </strong>{" "}
              →{" "}
              <strong className="ay-text-green-600">
                {selectedTargetMaterial}
              </strong>
            </p>
            <button
              type="button"
              onClick={handleAddMapping}
              className="ay-px-4 ay-py-2 ay-bg-gradient-to-r ay-from-blue-500 ay-to-green-500 ay-text-white ay-rounded-lg ay-hover:ay-from-blue-600 ay-hover:ay-to-green-600 ay-transition-colors ay-shadow"
            >
              ✨ Create Mapping
            </button>
          </div>
        ) : (
          <p className="ay-text-xs ay-text-gray-500">
            Select one material from each side to create a mapping
          </p>
        )}
      </div>
    </div>
  )
}

export default TypeLinksComponent
